import React, { useCallback, useEffect, useState } from 'react';
import Layout from '../../shared/components/Layout';
import Pagination from '../../shared/components/Pagination';
import UserDetailModal from '../../shared/components/UserDetailModal';
import { extractErrorMessage } from '../../shared/utils/errors';
import {
  FinanceCategory,
  FinanceDirection,
  FinanceOperation,
  FinancePlatformSummary,
  FinanceSummary,
  PaginatedResponse,
} from '../../shared/types';
import { formatUserLabel, useUserNames } from '../../shared/hooks/useUserNames';
import {
  OperationFilters,
  getOperationsSummary,
  getPlatformSummary,
  listOperations,
} from './api';
import OperationDetailModal from './OperationDetailModal';
import PlatformSummaryCards from './PlatformSummaryCards';

const CURRENCY_OPTIONS = ['', 'BTC', 'ETH', 'USDT', 'TRX', 'USD'];

const CATEGORY_OPTIONS: Array<{ value: FinanceCategory | ''; label: string }> = [
  { value: '', label: 'All categories' },
  { value: 'p2p', label: 'P2P' },
  { value: 'swap', label: 'Swap' },
  { value: 'withdrawal', label: 'Withdrawal' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'other', label: 'Other' },
];

const CATEGORY_LABELS: Record<string, string> = {
  p2p: 'P2P',
  swap: 'Swap',
  withdrawal: 'Withdrawal',
  deposit: 'Deposit',
  other: 'Other',
};

const CATEGORY_BADGES: Record<string, string> = {
  p2p: 'bg-indigo-500/15 text-indigo-300',
  swap: 'bg-sky-500/15 text-sky-300',
  withdrawal: 'bg-amber-500/15 text-amber-300',
  deposit: 'bg-emerald-500/15 text-emerald-300',
  other: 'bg-slate-500/15 text-slate-300',
};

const TYPE_LABELS: Record<string, string> = {
  credit: 'Credit',
  debit: 'Debit',
  escrow_hold: 'Escrow hold',
  escrow_release_credit: 'Escrow release (received)',
  escrow_release_debit: 'Escrow release (sent)',
  escrow_cancel: 'Escrow cancelled',
  escrow_adjust_increase: 'Escrow increased',
  escrow_adjust_decrease: 'Escrow decreased',
};

const SMALLEST_UNIT_DIVISOR = 100_000_000;

const formatCryptoAmount = (amount: number | null | undefined): string => {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '0';
  return (amount / SMALLEST_UNIT_DIVISOR).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 8,
  });
};

const formatUsd = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

const directionPrefix = (direction: FinanceDirection): string =>
  direction === 'in' ? '+' : direction === 'out' ? '−' : '';

const directionClass = (direction: FinanceDirection): string =>
  direction === 'in'
    ? 'text-emerald-400'
    : direction === 'out'
      ? 'text-rose-400'
      : 'text-amber-400';

const OperationsPage: React.FC = () => {
  const [operations, setOperations] =
    useState<PaginatedResponse<FinanceOperation> | null>(null);
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [platformSummary, setPlatformSummary] = useState<FinancePlatformSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSummaryLoading, setIsSummaryLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [filters, setFilters] = useState<OperationFilters>({});
  const [draftFilters, setDraftFilters] = useState<OperationFilters>({});

  const [profileUserId, setProfileUserId] = useState<number | null>(null);
  const [selectedOperation, setSelectedOperation] = useState<FinanceOperation | null>(null);

  const load = useCallback(async (page: number, filtersToUse: OperationFilters) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listOperations({ ...filtersToUse, page, per_page: 20 });
      setOperations(data);
    } catch (err) {
      setError(extractErrorMessage(err, 'Failed to load operations'));
      setOperations(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async (filtersToUse: OperationFilters) => {
    setIsSummaryLoading(true);
    const [summaryResult, platformResult] = await Promise.allSettled([
      getOperationsSummary(filtersToUse),
      getPlatformSummary({
        currency: filtersToUse.currency,
        date_from: filtersToUse.date_from,
        date_to: filtersToUse.date_to,
      }),
    ]);
    setSummary(summaryResult.status === 'fulfilled' ? summaryResult.value : null);
    setPlatformSummary(platformResult.status === 'fulfilled' ? platformResult.value : null);
    setIsSummaryLoading(false);
  }, []);

  useEffect(() => {
    load(currentPage, filters);
  }, [currentPage, filters, load]);

  useEffect(() => {
    loadSummary(filters);
  }, [filters, loadSummary]);

  const applyFilters = () => {
    setFilters(draftFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setDraftFilters({});
    setFilters({});
    setCurrentPage(1);
  };

  const userIds: number[] = [];
  if (operations?.data) {
    for (const op of operations.data) userIds.push(op.user_id);
  }
  const userNames = useUserNames(userIds);

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-2xl font-bold text-white">Money Operations</h1>
          <p className="text-slate-400 mt-1">
            Every money movement on the platform — P2P escrow, swaps, withdrawals and deposits.
            Filter by user to see per-user totals.
          </p>
        </div>

        <PlatformSummaryCards summary={platformSummary} isLoading={isSummaryLoading} />

        {!isSummaryLoading && summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            <div className="card p-4 border-indigo-500/30">
              <p className="text-xs uppercase tracking-wider text-slate-500">Total</p>
              <p className="text-2xl font-bold text-white mt-1">
                {summary.overall.operations.toLocaleString('en-US')}
              </p>
              <p className="text-xs text-slate-500 mt-2">operations</p>
              <div className="mt-2 space-y-0.5 text-xs font-mono">
                <p className="text-emerald-400">in: {formatUsd(summary.overall.usd_in)}</p>
                <p className="text-rose-400">out: {formatUsd(summary.overall.usd_out)}</p>
              </div>
            </div>
            {summary.categories.map((cat) => (
              <div key={cat.category} className="card p-4">
                <div className="flex items-center justify-between">
                  <span
                    className={`px-2 py-0.5 rounded text-xs font-medium ${
                      CATEGORY_BADGES[cat.category] ?? CATEGORY_BADGES.other
                    }`}
                  >
                    {CATEGORY_LABELS[cat.category] ?? cat.category}
                  </span>
                  <span className="text-xs text-slate-500">
                    {cat.operations.toLocaleString('en-US')} ops
                  </span>
                </div>
                <div className="mt-3 space-y-1 text-xs font-mono">
                  {cat.currencies.map((c) => (
                    <div key={c.currency} className="flex justify-between gap-2">
                      <span className="text-slate-400">{c.currency}</span>
                      <span className="text-right">
                        {c.in > 0 && (
                          <span className="text-emerald-400">+{formatCryptoAmount(c.in)}</span>
                        )}
                        {c.in > 0 && c.out > 0 && <span className="text-slate-600"> / </span>}
                        {c.out > 0 && (
                          <span className="text-rose-400">−{formatCryptoAmount(c.out)}</span>
                        )}
                        {c.in === 0 && c.out === 0 && (
                          <span className="text-amber-400" title="Held / released holds">
                            {formatCryptoAmount(c.hold)}
                          </span>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 pt-2 border-t border-slate-800/60 text-xs font-mono space-y-0.5">
                  <p className="text-emerald-400">in: {formatUsd(cat.usd_in)}</p>
                  <p className="text-rose-400">out: {formatUsd(cat.usd_out)}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="card p-4 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-7 gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">User ID</label>
            <input
              type="number"
              min={1}
              value={draftFilters.user_id ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, user_id: e.target.value || undefined }))
              }
              className="input py-2"
              placeholder="e.g. 42"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Category</label>
            <select
              value={draftFilters.category ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({
                  ...p,
                  category: (e.target.value as FinanceCategory | '') || undefined,
                }))
              }
              className="input py-2"
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt.value || 'all'} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">Currency</label>
            <select
              value={draftFilters.currency ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, currency: e.target.value || undefined }))
              }
              className="input py-2"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c || 'all-cur'} value={c}>
                  {c || 'Any'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">From</label>
            <input
              type="date"
              value={draftFilters.date_from ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, date_from: e.target.value || undefined }))
              }
              className="input py-2"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-400 mb-1">To</label>
            <input
              type="date"
              value={draftFilters.date_to ?? ''}
              onChange={(e) =>
                setDraftFilters((p) => ({ ...p, date_to: e.target.value || undefined }))
              }
              className="input py-2"
            />
          </div>
          <div className="flex gap-2 lg:col-span-2">
            <button type="button" onClick={applyFilters} className="btn btn-primary py-2 flex-1">
              Apply
            </button>
            <button type="button" onClick={clearFilters} className="btn btn-secondary py-2">
              Clear
            </button>
          </div>
        </div>

        <div className="card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-4 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin" />
            </div>
          ) : error ? (
            <div className="p-6 text-center">
              <p className="text-red-400">{error}</p>
            </div>
          ) : !operations?.data.length ? (
            <div className="p-12 text-center text-slate-400">No operations found.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="table-header">Date</th>
                      <th className="table-header">User</th>
                      <th className="table-header">Category</th>
                      <th className="table-header">Operation</th>
                      <th className="table-header text-right">Amount</th>
                      <th className="table-header text-right">Balance after</th>
                      <th className="table-header">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {operations.data.map((op) => (
                      <tr
                        key={op.id}
                        className="hover:bg-slate-800/30 transition-colors cursor-pointer"
                        onClick={() => setSelectedOperation(op)}
                      >
                        <td className="table-cell text-slate-400 text-sm whitespace-nowrap">
                          {new Date(op.created_at).toLocaleString()}
                          <p className="text-xs text-slate-600 font-mono">#{op.id}</p>
                        </td>
                        <td className="table-cell whitespace-nowrap">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setProfileUserId(op.user_id);
                            }}
                            className="text-sky-400 hover:text-sky-300 hover:underline transition-colors"
                          >
                            {formatUserLabel(op.user_id, userNames)}
                          </button>
                          <p className="text-xs text-slate-500 font-mono">#{op.user_id}</p>
                        </td>
                        <td className="table-cell">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${
                              CATEGORY_BADGES[op.category] ?? CATEGORY_BADGES.other
                            }`}
                          >
                            {CATEGORY_LABELS[op.category] ?? op.category}
                          </span>
                        </td>
                        <td className="table-cell text-slate-300 text-sm whitespace-nowrap">
                          {TYPE_LABELS[op.type] ?? op.type}
                        </td>
                        <td
                          className={`table-cell text-right font-mono whitespace-nowrap ${directionClass(op.direction)}`}
                        >
                          {directionPrefix(op.direction)}
                          {formatCryptoAmount(op.amount)}{' '}
                          <span className="text-slate-500">{op.currency}</span>
                        </td>
                        <td className="table-cell text-right font-mono text-slate-400 whitespace-nowrap">
                          {formatCryptoAmount(op.balance_after)}
                        </td>
                        <td className="table-cell max-w-[280px]">
                          <p className="text-sm text-slate-300 truncate" title={op.description ?? ''}>
                            {op.description ?? '—'}
                          </p>
                          {op.reference_id && (
                            <p
                              className="text-xs text-slate-600 font-mono truncate"
                              title={op.reference_id}
                            >
                              {op.reference_id}
                            </p>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {operations.last_page > 1 && (
                <div className="p-4 border-t border-slate-800 flex items-center justify-between gap-4">
                  <p className="text-xs text-slate-500">
                    {operations.total.toLocaleString('en-US')} operation
                    {operations.total === 1 ? '' : 's'}
                  </p>
                  <Pagination
                    currentPage={operations.current_page}
                    lastPage={operations.last_page}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <OperationDetailModal
        isOpen={selectedOperation !== null}
        onClose={() => setSelectedOperation(null)}
        operation={selectedOperation}
        userLabel={
          selectedOperation
            ? formatUserLabel(selectedOperation.user_id, userNames)
            : ''
        }
        onViewUser={(userId) => {
          setSelectedOperation(null);
          setProfileUserId(userId);
        }}
      />

      <UserDetailModal
        isOpen={profileUserId !== null}
        onClose={() => setProfileUserId(null)}
        userId={profileUserId}
      />
    </Layout>
  );
};

export default OperationsPage;
